import {
  INTEGRATION_NAMES,
  ORG_PLAN_NAMES,
  type IntegrationName,
  type OrgOnboardingStatus,
  type OrgPlanName,
} from "./types";

/**
 * Whether the org is on a plan that requires setup — a recognized
 * {@link OrgPlanName} on `organization.plan.type`. This is a coarse signal only:
 * whole-flow completion is derived from the per-step status at runtime (see
 * `deriveSetupFlow` in `@tellescope/onboarding`), which this lower tier cannot
 * compute, so the dialog provider folds it in separately.
 *
 * @param planType The raw `organization.plan?.type` value.
 * @returns `true` when the org is on a recognized onboarding plan.
 */
export const needsOnboarding = (planType: OrgPlanName | string | undefined): boolean =>
  parseOrgPlanName(planType) !== undefined;

/**
 * The integrations the user selected during setup, in {@link INTEGRATION_NAMES}
 * order. An integration is selected iff its key is present on the record (any
 * value, including `false`).
 *
 * @param status An {@link OrgOnboardingStatus} record.
 * @returns Selected integration names in canonical order.
 */
export const getSelectedIntegrations = (status: OrgOnboardingStatus): IntegrationName[] =>
  INTEGRATION_NAMES.filter((name) => name in (status ?? {}));

/**
 * Narrows an unknown plan-type value (e.g. `organization.plan?.type`) into a
 * known {@link OrgPlanName}.
 *
 * @param value The raw plan type.
 * @returns The matching {@link OrgPlanName}, or `undefined` if unrecognized.
 */
export const parseOrgPlanName = (value: unknown): OrgPlanName | undefined =>
  ORG_PLAN_NAMES.find((name) => name === value);
